<template lang="pug">
.gc-checkout
  Breadcrumbs(:list="breadcrumbs")

  .gc-checkout__header
    img.gc-checkout__header-img(:src="`https://cdn.gaming-goods.com/images/games/currencies/${brand}.png`" alt="cover")
    .gc-checkout__title
      span {{ $t('addingFunds') }}:
      span {{ brand }}

  .gc-checkout__main
    .gc-checkout__block
      .gc-checkout__block-title {{ $t("enterUserID") }}

      .gc-checkout__fields
        .gc-checkout__field
          .gc-checkout__field-label USER ID
          FormInput(v-model="userId")
        .gc-checkout__field(v-if="needZoneId")
          .gc-checkout__field-label ZONE ID
          FormInput(v-model="zoneId")

      .gc-checkout__block-title {{ $t("chooseProduct") }}

      .gc-checkout__products
        .gc-checkout__product(v-for="product in products" :key="product.id" :class="{'gc-checkout__product--active': product.id === productId}" @click="setActive(product.id)")
          | {{ product.title }}

      .gc-checkout__block-title {{ $t("payMethod") }}

      .gc-checkout__fields.gc-checkout__fields--no-border
        .gc-checkout__field
          BaseSelect(
            :options="payMethods"
            v-model="payMethod"
            :with-empty-value="false"
          )

    .gc-checkout__block
      .gc-checkout__block-title
        span {{ $t('yourOrder') }}
        span.gc-checkout__title-additional {{ $t("nItems", {n: 1}) }}

      .gc-checkout__fields
        .gc-checkout__lineproduct
          span.gc-checkout__lineproduct-name {{ productTitle }} •&nbsp;
            span.gc-checkout__lineproduct-amount {{ $t("nPcs", {n: 1}) }}
          Currency(type="money" size="16" is-reversed real)
            span.gc-checkout__lineproduct-price {{ productPrice }}

      .gc-checkout__fields
        .gc-checkout__details-subtitle {{ $t("yourPriceWithDiscount") }}

        .gc-checkout__slider-container
          input.gc-checkout__slider(type="range" :min="+productMinPrice" :max="+productPrice" v-model="cartPriceInSlider" step="0.01")

        .gc-checkout__your-price-block
          p.gc-checkout__your-price {{ $t("yourPrice") }}:
          .gc-checkout__your-price-amount
            Currency(type="money" is-reversed size="16" real) {{ total }}
            span.gc-checkout__your-price-amount-separator +
            .gc-checkout__your-price-amount-coins
              Currency.gc-checkout__your-price-amount-coins-amount(type="coins" is-reversed size="20") {{ coins }}
              .gc-checkout__your-price-amount-coins-max MAX

      .gc-checkout__fields.gc-checkout__fields--no-border
        .gc-checkout__pair
          .gc-checkout__pair-key--big {{ $t("totalPay") }}:
          Currency(type="money" size="16" is-reversed real)
            .gc-checkout__pair-value {{ total }}

        .gc-checkout__checkbox
          CheckboxInput(v-model="checkbox")
          span.gc-checkout__checkbox-label(v-html="$t('marketplaceCheckoutCheckboxText')")

        Button.gc-checkout__button(v-if="showButton" is-primary :disabled="checkoutButtonDisabled" @click="buy") {{ $t("buy") }}

        p.gc-checkout__address {{$t('ourCompanyAddress')}}: Solian Enterprises Limited. Registered number: HE 412444. Emmanouil road 44, KIRZIS CENTER, 3031 Limassol, Cyprus
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs'
import { mapActions } from 'vuex'
import FormInput from '~/components/FormInput/FormInput'
import CheckboxInput from '~/components/CheckboxInput/CheckboxInput'
import BaseSelect from '~/components/BaseSelect/BaseSelect'
import StoreService from '~/services/StoreService'

export default {
  components: {
    Breadcrumbs,
    FormInput,
    BaseSelect,
    CheckboxInput
  },

  data () {
    return {
      userId: '',
      zoneId: '',
      products: [],
      productId: undefined,
      checkbox: false,
      payMethod: 'BANKCARD',
      cartPriceInSlider: undefined,
      showButton: true,
      payMethods: [
        {
          title: this.$t('creditCard'),
          value: 'BANKCARD'
        },
        {
          title: this.$t('siteBalance'),
          value: 'BALANCE'
        }
      ]
    }
  },

  computed: {
    breadcrumbs () {
      return [
        { title: this.$t('store'), to: '/store/game-currencies' },
        { title: this.$t('checkout') }
      ]
    },

    checkoutButtonDisabled () {
      return !this.checkbox || !this.userId || this.userId === '' || (this.needZoneId && (!this.zoneId || this.zoneId === '')) || !this.productId
    },

    needZoneId () {
      return this.brand === 'MobileLegends'
    },

    brand () {
      return this.$route.query.game
    },

    productTitle () {
      return this.products?.find(p => p.id === this.productId)?.title || ''
    },

    productPrice () {
      return ((this.products?.find(p => p.id === this.productId)?.max_price || 0) / 100)?.toFixed(2)?.replace('.00', '') || '___'
    },

    productMinPrice () {
      return ((this.products?.find(p => p.id === this.productId)?.min_price || 0) / 100)?.toFixed(2)?.replace('.00', '') || '___'
    },

    coinsForSlider () {
      if (this.cartPriceInSlider !== undefined) {
        return (+this.productPrice - this.cartPriceInSlider) * 1000000
      }
      return 0
    },

    coins () {
      return Math.ceil(this.coinsForSlider || 0)
    },

    total () {
      return (+(this.cartPriceInSlider || 0)).toFixed(2).replace('.00', '')
    }
  },

  mounted () {
    this.loadProducts()
  },

  methods: {
    ...mapActions('modal', ['showModal']),

    async loadProducts () {
      const { products } = (await StoreService.getGameCurrencies(this.$axios, this.brand))?.data?.data
      this.products = products
      this.cartPriceInSlider = +this.productPrice
    },

    setActive (id) {
      this.productId = id
      this.cartPriceInSlider = +this.productPrice
    },

    buy () {
      if (this.checkoutButtonDisabled) {
        return
      }

      if (!this.showButton) {
        return
      }

      this.showButton = false

      try {
        StoreService.createVirtualOrder(this.$axios, {
          productId: this.productId,
          coins: this.coins,
          userId: this.userId,
          zoneId: this.zoneId,
          method: this.payMethod
        }, `${location.protocol}//${location.host}/marketplace/order`)
          .then((response) => {
            if (response.data?.returning_url === '') {
              this.$router.push(this.localeLocation('/profile/orders'))
            } else if (response.data?.returning_url) {
              window.location.replace(response.data.returning_url)
            }
          })
          .catch((err) => {
            this.showModal({
              component: 'ModalMessage',
              data: {
                type: 'error',
                text: this.$t(`errorCode${err.response?.data?.code}`)
              }
            })
          })
      } catch (e) {
        console.log(e)
        this.showModal({
          component: 'ModalMessage',
          data: {
            type: 'error',
            text: this.$t('error')
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.gc-checkout {
  padding: 16px;
  color: $white;

  @media (min-width: $breakpointDesktopWide) {
    padding: 44px 115px;
  }

  &__header {
    &-img {
      height: 48px;
      width: 48px;
      object-fit: cover;
      border-radius: 8px;
      margin-bottom: 8px;
    }
  }

  &__title {
    display: grid;
    grid-template-columns: 1fr;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
  }

  &__main {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 24px;
    margin-top: 12px;

    @media (min-width: $breakpointDesktopWide) {
      grid-template-columns: 7fr 5fr;
    }
  }

  &__block {
    background: $blue-grey70;
    border-radius: 16px;
    height: max-content;

    &-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      color: $white;
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
      border-bottom: 1px solid $blue-grey40;

      &-additional {
        font-size: 16px;
        font-weight: 500;
        line-height: 20px;
      }
    }
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 16px;
    padding: 16px;
    border-bottom: 1px solid $blue-grey40;

    &--no-border {
      border: none;
    }
  }

  &__field {
    &-label {
      margin-bottom: 4px;
      color: $blue-grey10;
      font-size: 12px;
      font-weight: 500;
      line-height: 100%;
      letter-spacing: 0.24px;
      text-transform: uppercase;
    }
  }

  &__products {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 8px;
    align-items: center;
    padding: 20px 16px;

    @media (min-width: $breakpointTablet) {
      grid-template-columns: 1fr 1fr;
    }

    @media (min-width: $breakpointDesktopWide) {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  &__product {
    background: $blue-grey50;
    border-radius: 12px;
    padding: 16px;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    border: 2px solid $blue-grey50;
    cursor: pointer;

    &--active {
      border: 2px solid $primary60;
      cursor: default;
    }
  }

  &__lineproduct {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;

    &-name {
      color: $white;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
    }

    &-amount {
      color: $blue-grey10;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
    }

    &-price {
      color: $white;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
    }
  }

  &__pair {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &--sub {
      margin-left: 20px;
    }

    &-key {
      color: $blue-grey10;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;

      &--big {
        color: $white;
        font-size: 20px;
        font-weight: 700;
        line-height: 28px;
      }
    }

    &-value {
      color: $white;
      font-size: 16px;
      font-weight: 700;
      line-height: 22px;
    }
  }

  &__checkbox {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;

    &-label {
      color: $white;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
    }
  }

  &__button {
    width: 100%;

    &:disabled {
      opacity: 0.4;
    }
  }

  &__address {
    color: $blue-grey10;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
  }

  &__slider {
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
    height: 8px;
    background: $blue-grey50;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 32px;
      height: 32px;
      background: $primary60;
      border-radius: 50%;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 32px;
      height: 32px;
      background: $primary60;
      border-radius: 50%;
      cursor: pointer;
    }

    &-container {
      width: 100%;
      margin: 24px 0;
    }
  }

  &__your-price {
    margin-right: 8px;
    color: $blue-grey10;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;

    &-block {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    &-amount {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      height: 32px;
      color: $white;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;

      &-separator {
        padding: 0 8px;
        color: $blue-grey10;
        font-size: 20px;
        font-weight: 500;
        line-height: 28px;
      }

      &-coins {
        display: grid;
        align-items: center;
        grid-template-columns: max-content max-content;

        &-amount {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 4px 12px 4px 8px;
          background: $blue-grey50;
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }

        &-max {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 4px 8px;
          color: $primary60;
          background: rgba($primary60, 0.2);
          font-size: 18px;
          font-style: normal;
          font-weight: 500;
          line-height: 22px;
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      }
    }
  }

  &__details {
    padding: 16px;

    @media (min-width: $breakpointDesktopWide) {
      padding: 24px;
    }

    &-head {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &-label {
        color: $white;
        font-size: 16px;
        font-weight: 700;
        line-height: 24px;
      }

      &-amount {
        color: $white;
        font-size: 16px;
        font-weight: 700;
        line-height: 24px;
      }
    }

    &-subtitle {
      margin: 20px 0 16px;
      color: $white;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
    }
  }
}
</style>
